import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useMemo, useState } from "react";
import { Button, Form } from "reactstrap";
import { useMeCore } from "../../../../cores/me";
import { useIntl } from "react-intl";
import { useBoardCore } from "../../../../cores/boards";
import { InputWembii } from "../../InputWembii";
import { LabelWembii } from "../../LabelWembii";
import { PanelWembii } from "../../PanelWembii";
import { Board } from "../../../../cores/boards/interfaces/api/get-board";
import { BoardType } from "../../../../cores/boards/constants/board-type.constant";
import {
  CrmListColumnTemplate,
  getCrmListColumnTemplates,
} from "../../../../cores/boards/constants/crm-list-column-templates";

interface Props {
  board: Board | null;
}

const getStageAccent = (percentage?: number): string => {
  if (percentage === undefined) return "#64748b";
  if (percentage >= 100) return "#16a34a";
  if (percentage === 0) return "#dc2626";
  if (percentage >= 70) return "#0b65c2";
  if (percentage >= 40) return "#7c3aed";
  return "#d97706";
};

export default function AddListContainer({ board }: Props) {
  const intl = useIntl();
  const { company } = useMeCore();
  const { postList, putList, lists } = useBoardCore();
  const [addList, setAddList] = useState(false);
  const [listName, setListName] = useState("");
  const [creatingId, setCreatingId] = useState<string | null>(null);

  const isBusinessBoard = board?.type === BoardType.BUSINESS;
  const columnTemplates = useMemo(
    () => getCrmListColumnTemplates(board?.type),
    [board?.type]
  );

  const existingListNames = useMemo(
    () => new Set(lists.map((list) => list.name.trim().toLowerCase())),
    [lists]
  );

  const availableTemplates = useMemo(
    () =>
      columnTemplates.filter(
        (template) =>
          !existingListNames.has(
            intl.formatMessage({ id: template.nameKey }).trim().toLowerCase()
          )
      ),
    [columnTemplates, existingListNames, intl]
  );

  const toggleAddList = useCallback(() => {
    setListName("");
    setAddList((open) => !open);
  }, []);

  const createList = useCallback(
    async (name: string, template?: CrmListColumnTemplate) => {
      if (!company?.id || !board?.id || !name.trim()) return;

      setCreatingId(template?.id ?? "custom");
      try {
        const list = await postList(company.id, name.trim(), board.id);

        if (
          list?.id &&
          isBusinessBoard &&
          template?.percentage !== undefined
        ) {
          await putList(company.id, board.id, list.id, {
            percentage: template.percentage,
            addToProbability: template.addToProbability ?? true,
          });
        }

        setListName("");
        setAddList(false);
      } finally {
        setCreatingId(null);
      }
    },
    [company, board, postList, putList, isBusinessBoard]
  );

  const onFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await createList(listName);
  };

  const onTemplateClick = useCallback(
    (template: CrmListColumnTemplate) => {
      createList(intl.formatMessage({ id: template.nameKey }), template);
    },
    [createList, intl]
  );

  const renderTemplates = () => (
    <div className="crm-add-list-templates">
      <div className="crm-add-list-templates__header">
        <p className="crm-add-list-templates__title">
          {intl.formatMessage({
            id: isBusinessBoard
              ? "app.crmList.template.businessHint"
              : "app.crmList.template.activityHint",
          })}
        </p>
        <span className="crm-add-list-templates__count">
          {availableTemplates.length}
        </span>
      </div>

      {availableTemplates.length === 0 ? (
        <p className="crm-add-list-templates__empty">
          {intl.formatMessage({ id: "app.crmList.template.allUsed" })}
        </p>
      ) : (
        <div className="crm-add-list-templates__list">
          {availableTemplates.map((template) => {
            const accent = getStageAccent(template.percentage);

            return (
              <button
                key={template.id}
                type="button"
                className="crm-add-list-template-row"
                disabled={creatingId !== null}
                onClick={() => onTemplateClick(template)}
              >
                <span
                  className="crm-add-list-template-row__accent"
                  style={{ backgroundColor: accent }}
                />
                <span className="crm-add-list-template-row__label">
                  {intl.formatMessage({ id: template.nameKey })}
                </span>
                {template.percentage !== undefined && (
                  <span className="crm-add-list-template-row__meta">
                    {template.percentage}%
                  </span>
                )}
                <FontAwesomeIcon
                  icon={["fas", "plus"]}
                  className="crm-add-list-template-row__icon"
                />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <PanelWembii className="mx-2 crm-add-list-column panel-list panel-wembii card-container">
      <div className="crm-add-list-column__body">
        {!addList ? (
          <>
            <button
              type="button"
              className="crm-add-list-column__trigger"
              onClick={toggleAddList}
            >
              <span className="crm-add-list-column__icon" aria-hidden>
                <FontAwesomeIcon icon={["fas", "plus"]} />
              </span>
              <span className="crm-add-list-column__copy">
                <span className="crm-add-list-column__label">
                  {intl.formatMessage({ id: "app.activity.addList" })}
                </span>
                <span className="crm-add-list-column__hint">
                  {intl.formatMessage({ id: "app.crmList.addColumnHint" })}
                </span>
              </span>
            </button>

            <button
              type="button"
              className="crm-add-list-column__custom-btn"
              onClick={toggleAddList}
            >
              <FontAwesomeIcon icon={["fas", "pen"]} className="mr-2" />
              {intl.formatMessage({ id: "app.crmList.customColumn" })}
            </button>
          </>
        ) : (
          <div className="crm-add-list-column__form">
            <Form onSubmit={onFormSubmit}>
              <LabelWembii forid="crm-add-list-title" required>
                {intl.formatMessage({ id: "app.activity.titleList" })}
              </LabelWembii>
              <InputWembii
                type="text"
                id="crm-add-list-title"
                icon={
                  <FontAwesomeIcon
                    icon={["fas", "times"]}
                    size="1x"
                    className="pointer text-muted"
                    onClick={toggleAddList}
                  />
                }
                value={listName}
                onChange={setListName}
              />
              <Button
                color="primary"
                block
                className="mt-2"
                disabled={!listName.trim() || creatingId !== null}
              >
                {intl.formatMessage({ id: "app.activity.add" })}
              </Button>
            </Form>
          </div>
        )}

        {isBusinessBoard && availableTemplates.length > 0 && (
          <div className="crm-add-list-column__preview" aria-hidden>
            {availableTemplates.slice(0, 4).map((template) => (
              <span
                key={template.id}
                className="crm-add-list-column__preview-bar"
                style={{
                  width: `${Math.max(28, template.percentage ?? 20)}%`,
                  backgroundColor: getStageAccent(template.percentage),
                }}
              />
            ))}
          </div>
        )}

        {renderTemplates()}
      </div>
    </PanelWembii>
  );
}
